import { useEffect, useRef } from 'react';
import { authFetch, createIntakeSession, updateIntakeSession } from '../../api';
import { getVoiceLanguage } from '../../lib/voiceLanguages';

/**
 * Debounced autosave of the in-progress Verify/Register form into
 * `intake_sessions` (mode='manual') — reusing the exact "not yet moved into
 * Visit Workflow, listable, resumable" mechanism Voice Intake already built
 * (see VoiceIntake.tsx's own comment on why conversion is deferred, and
 * VisitWorkflow.tsx's `completeStage`/`mergeAsReturningPatient`, which
 * already mark whichever session `window.__voiceIntakeSessionId` points at
 * 'converted' the moment registration truly succeeds, and `backToList`,
 * which already un-links it on an explicit reset). All of that plumbing
 * already existed and already anticipated this — it was only ever wired up
 * for a visit that started from an actual voice recording. A visit typed in
 * by hand (the common case) had nothing writing to `intake_sessions` at all,
 * so a browser closed mid-Verify or mid-Register lost everything with no way
 * back. This fills that one gap rather than inventing a second recovery path.
 *
 * If this visit actually did start from Voice Intake, `window.__voiceIntakeSessionId`
 * is already set — this reuses and updates that same session instead of
 * creating a second, orphaned one.
 *
 * Three save triggers, because a debounce alone leaves a real gap: a browser
 * closed less than DEBOUNCE_MS after the last keystroke loses that keystroke
 * (found live — a nurse typed a mobile number and closed the tab before the
 * quiet window elapsed, and only the earlier-typed name survived).
 *   1. The debounced timer below — the common case, saves shortly after
 *      typing pauses.
 *   2. `beforeunload`/`pagehide` — an actual tab close/refresh/navigation
 *      away. Uses `authFetch` with `keepalive: true` so the request is
 *      allowed to complete after the page starts tearing down, unlike a
 *      plain fetch which the browser can abort mid-flight; `keepalive`
 *      still carries the Authorization header (unlike `navigator.sendBeacon`,
 *      which cannot set custom headers at all, ruling it out for an
 *      authenticated endpoint).
 *   3. Unmount flush — leaving this stage in-app (Verify → Register, or
 *      "Close visit"). Deliberately UPDATE-only, never create: at the moment
 *      Register unmounts after a *successful* registration, VisitWorkflow.tsx
 *      has already cleared `window.__voiceIntakeSessionId` to null (the
 *      session was just marked 'converted') — creating a fresh session here
 *      would silently leave a stray, never-converted duplicate behind for
 *      data that's already safely a real patient record.
 */
export function useIntakeDraft(fields: Record<string, string | undefined>): void {
  const savingRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fieldsRef = useRef(fields);
  fieldsRef.current = fields;
  // Debounce on a stable serialization of the fields rather than the object
  // identity, which changes on every keystroke's setState regardless.
  const snapshot = JSON.stringify(fields);

  const cleanFields = () => Object.fromEntries(Object.entries(fieldsRef.current).filter(([, v]) => (v || '').toString().trim() !== ''));

  const save = (clean: Record<string, string | undefined>) => {
    if (savingRef.current) return;
    savingRef.current = true;
    const existingId = window.__voiceIntakeSessionId;
    const run = existingId
      ? updateIntakeSession(existingId, { parsed_data: clean })
      : createIntakeSession({ mode: 'manual', language_code: getVoiceLanguage(), parsed_data: clean })
          .then((session: { id: number }) => { window.__voiceIntakeSessionId = session.id; });
    run.catch(() => {}).finally(() => { savingRef.current = false; });
  };

  useEffect(() => {
    const clean = cleanFields();
    if (!Object.keys(clean).length) return;

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => save(clean), 800);

    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [snapshot]);

  useEffect(() => {
    const flushOnClose = () => {
      const clean = cleanFields();
      if (!Object.keys(clean).length) return;
      const existingId = window.__voiceIntakeSessionId;
      const body = existingId
        ? { parsed_data: clean }
        : { mode: 'manual', language_code: getVoiceLanguage(), parsed_data: clean };
      authFetch(existingId ? `/api/intake/${existingId}` : '/api/intake', {
        method: existingId ? 'PATCH' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        keepalive: true,
      }).catch(() => {});
    };
    window.addEventListener('beforeunload', flushOnClose);
    window.addEventListener('pagehide', flushOnClose);
    return () => {
      window.removeEventListener('beforeunload', flushOnClose);
      window.removeEventListener('pagehide', flushOnClose);
      // In-app stage change (not a tab close) — only ever update an already-
      // existing draft, never create one; see this file's header comment on
      // why creating here is unsafe.
      const existingId = window.__voiceIntakeSessionId;
      if (!existingId) return;
      const clean = cleanFields();
      if (!Object.keys(clean).length) return;
      updateIntakeSession(existingId, { parsed_data: clean }).catch(() => {});
    };
  }, []);
}
